import React, { useState } from "react";
import { Users, X, Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

const TrainingPlayersModal = ({
  isDarkMode,
  event,
  allPlayers,
  onClose,
  onSavePlayersList,
  onCreateAlternative,
}) => {
  const { t } = useTranslation();
  const [selectedPlayers, setSelectedPlayers] = useState(event?.player_ids || []);
  const [searchQuery, setSearchQuery] = useState("");
  const [saving, setSaving] = useState(false);

  const filteredPlayers = allPlayers.filter((p) => {
    const name =
      p.name ||
      p.full_name ||
      `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() ||
      "";
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleTogglePlayer = (playerId) => {
    setSelectedPlayers((prev) =>
      prev.includes(playerId)
        ? prev.filter((id) => id !== playerId)
        : [...prev, playerId]
    );
  };

  const handleSelectAll = () => {
    setSelectedPlayers(filteredPlayers.map((p) => p.id));
  };

  const handleDeselectAll = () => {
    setSelectedPlayers([]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSavePlayersList(event.id, selectedPlayers);
      toast.success(t("calendar_ui.players_updated"));
      onClose();
    } catch (error) {
   
      toast.error(error.message || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const getPlayerName = (p) =>
    p.name ||
    p.full_name ||
    `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() ||
    p.id;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className={`p-6 rounded-lg shadow-lg w-[600px] max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode ? "bg-gray-800 text-white" : "bg-white text-black"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users size={24} className="text-sky-500" />
            <div>
              <h2 className="text-lg font-bold">{t("calendar_ui.assigned_players")}</h2>
              <p className="text-sm opacity-70">
                {event?.label || event?.opponent?.name || t("calendar_ui.training")}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-600/30 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <div className="mb-3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("calendar_ui.search_player")}
            className={`w-full p-2 rounded-md border ${
              isDarkMode
                ? "bg-gray-900 border-gray-700 text-white"
                : "bg-white border-gray-300 text-black"
            }`}
          />
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={handleSelectAll}
            className={`px-3 py-1.5 text-sm rounded-md transition ${
              isDarkMode
                ? "bg-gray-700 text-white hover:bg-gray-600"
                : "bg-gray-200 text-black hover:bg-gray-300"
            }`}
          >
            {t("calendar_ui.select_all")}
          </button>
          <button
            onClick={handleDeselectAll}
            className={`px-3 py-1.5 text-sm rounded-md transition ${
              isDarkMode
                ? "bg-gray-700 text-white hover:bg-gray-600"
                : "bg-gray-200 text-black hover:bg-gray-300"
            }`}
          >
            {t("calendar_ui.deselect_all")}
          </button>
          <div className="ml-auto text-sm opacity-70 flex items-center">
            {t(
              selectedPlayers.length > 1
                ? "calendar_ui.selected_count_plural"
                : "calendar_ui.selected_count",
              { count: selectedPlayers.length }
            )}
          </div>
        </div>

        {/* Players List */}
        <div
          className={`flex-1 overflow-y-auto rounded-md border p-2 ${
            isDarkMode ? "border-gray-700 bg-gray-900" : "border-gray-300 bg-white"
          }`}
        >
          {filteredPlayers.length === 0 ? (
            <p className="text-sm opacity-70 text-center py-4">{t("calendar_ui.no_player_found")}</p>
          ) : (
            <div className="space-y-1">
              {filteredPlayers.map((p) => {
                const isSelected = selectedPlayers.includes(p.id);
                return (
                  <label
                    key={p.id}
                    className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition ${
                      isSelected
                        ? isDarkMode
                          ? "bg-sky-500/20 border border-sky-500"
                          : "bg-sky-100 border border-sky-400"
                        : isDarkMode
                        ? "hover:bg-gray-800"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleTogglePlayer(p.id)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{getPlayerName(p)}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Alternative Training Button */}
        <div className="mt-4 pt-3 border-t border-gray-600/30">
          <button
            onClick={() => onCreateAlternative(event)}
            className={`w-full px-4 py-2 rounded-md transition flex items-center justify-center gap-2 ${
              isDarkMode
                ? "bg-violet-600 text-white hover:bg-violet-700"
                : "bg-violet-500 text-white hover:bg-violet-600"
            }`}
          >
            <Plus size={18} />
            {t("calendar_ui.create_alt")}
          </button>
          <p className="text-xs opacity-70 mt-1 text-center">
            {t("calendar_ui.create_alt_hint")}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-3 border-t border-gray-600/30 mt-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className={`flex-1 px-4 py-2 rounded-md text-white font-medium transition ${
              saving
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {saving ? t("common.saving") : t("common.save")}
          </button>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-md transition ${
              isDarkMode
                ? "bg-gray-700 text-white hover:bg-gray-600"
                : "bg-gray-200 text-black hover:bg-gray-300"
            }`}
          >
            {t("common.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrainingPlayersModal;
